import {
  PathNavigationCapability,
  MapNavigationCapability,
  FreeViewCapability,
  NavigationalCapabilityBase,
  StationsNavigationCapability,
} from '@trimble/web3d-capabilities';
import { CapabilityRegistryContainer } from '@trimble/web3d';
import {
  SyncCoordinator,
  SyncFn,
  getActiveNavCap,
  syncAlignmentToAlignment,
  syncAlignmentToFreeView,
  syncAlignmentToMap,
  syncAlignmentToStation,
  syncNoOp,
  syncStationToAlignment,
  syncStationToFreeView,
  syncStationToMap,
  syncStationToStation,
} from '@trimble/pane-sync';

type CapKey = 'alignment' | 'station' | 'map' | 'freeView';

/**
 * Ordered `NavigationalCapabilityBase` subclass → {@link CapKey} pairing.
 *
 * Kept as a tuple (not a switch) because `classify()` needs `instanceof`
 * checks — union `switch` statements cannot discriminate class hierarchies.
 * `ImageCapabilityBase extends StationsNavigationCapability`, so the
 * `'station'` key covers the whole subtree (plain stations + Panorama + Planar).
 */
type CapabilityConstructor = new (
  ...args: never[]
) => NavigationalCapabilityBase;

const CAP_CLASSIFIERS: ReadonlyArray<readonly [CapabilityConstructor, CapKey]> =
  [
    [PathNavigationCapability, 'alignment'],
    [StationsNavigationCapability, 'station'],
    [MapNavigationCapability, 'map'],
    [FreeViewCapability, 'freeView'],
  ] as const;

/**
 * Compile-time assertion that every `CapKey` is covered by a classifier.
 * Adding a new `CapKey` without extending `CAP_CLASSIFIERS` fails to compile.
 */
type ClassifierKeys = (typeof CAP_CLASSIFIERS)[number][1];
type AssertAllKeysCovered = CapKey extends ClassifierKeys
  ? ClassifierKeys extends CapKey
    ? true
    : never
  : never;
const _capClassifierCoverage: AssertAllKeysCovered = true;
void _capClassifierCoverage;

function classify(
  cap: NavigationalCapabilityBase | undefined
): CapKey | undefined {
  if (!cap) return undefined;
  for (const [Constructor, key] of CAP_CLASSIFIERS) {
    if (cap instanceof Constructor) return key;
  }
  return undefined;
}

/**
 * Full routing table from source CapKey → target CapKey → SyncFn.
 *
 * Every entry is mono-source: the SyncFn declares its source dependency
 * in its first line via the matching `getXxxSyncPosition` helper, so the
 * table here is just a dispatch lookup with no adapter wiring.
 *
 * Using a non-`Partial` `Record` forces every (source, target) pair to be
 * explicitly routed. `freeView` / `map` source rows route to `syncNoOp`
 * for every target — those capabilities do not drive syncs today but are
 * kept in the table so the type system enforces exhaustiveness.
 */
const syncTable: Record<CapKey, Record<CapKey, SyncFn>> = {
  alignment: {
    alignment: syncAlignmentToAlignment,
    station: syncAlignmentToStation,
    map: syncAlignmentToMap,
    freeView: syncAlignmentToFreeView,
  },
  station: {
    alignment: syncStationToAlignment,
    station: syncStationToStation,
    map: syncStationToMap,
    freeView: syncStationToFreeView,
  },
  freeView: {
    alignment: syncNoOp,
    station: syncNoOp,
    map: syncNoOp,
    freeView: syncNoOp,
  },
  map: {
    alignment: syncNoOp,
    station: syncNoOp,
    map: syncNoOp,
    freeView: syncNoOp,
  },
};

const noOp: SyncCoordinator = { sync: syncNoOp };

export function resolveSyncCoordinator(
  sourcePane: CapabilityRegistryContainer,
  targetPane: CapabilityRegistryContainer
): SyncCoordinator {
  const sourceKey = classify(getActiveNavCap(sourcePane));
  const targetKey = classify(getActiveNavCap(targetPane));

  if (!sourceKey || !targetKey) return noOp;

  return { sync: syncTable[sourceKey][targetKey] };
}
